import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { format, parseISO } from 'date-fns'
import { ClassNames } from '@emotion/core'
import { Text } from 'shared/components'
import { InWinImage } from 'routes/shared/components'
import config from 'config'

import { MobileInWinModal } from './components/mobileInWinModal'
import { getDisplayScore } from './score'
import * as styles from './styles'

const getGameStatus = (game) => {
  if (game.resolved) {
    return 'Final'
  }

  const now = new Date()
  const gameTime = parseISO(game.startTime)

  if (gameTime > now) {
    return 'INWIN' // Upcoming game
  }

  return 'Live' // In progress
}

const formatGameTime = (startTime) => {
  if (!startTime) return ''

  try {
    const gameTime = parseISO(startTime)
    return format(gameTime, 'HH:mm')
  } catch (error) {
    return ''
  }
}

const getTeamLogo = (team) => {
  if (team?.logo) {
    return team.logo.startsWith('http')
      ? team.logo
      : `${config.assets.baseUrl}${team.logo}`
  }
  return null
}

const getTeamAbbreviation = (teamName, teamObj) => {
  if (teamObj?.teamName) {
    // Generate 3-letter abbreviation from team name
    const words = teamObj.teamName.split(' ')
    if (words.length >= 2) {
      return (
        words
          .slice(0, 2)
          .map((word) => word.charAt(0))
          .join('') +
        (words[0].length > 1 ? words[0].charAt(1).toUpperCase() : '')
      )
    }
    return teamObj.teamName.substring(0, 3).toUpperCase()
  }

  if (teamName) {
    return teamName.substring(0, 3).toUpperCase()
  }

  return 'TBD'
}

const getTeamColor = (teamAbbr) => {
  const teamColors = {
    CHI: '#DC143C', // Crimson red
    CLE: '#1D1160', // Cleveland Cavaliers purple
    GS: '#FFC72C', // Golden State Warriors gold
    IND: '#002D62', // Indiana Pacers navy
    DET: '#C8102E', // Detroit Pistons red
    BKN: '#000000', // Brooklyn Nets black
  }

  return teamColors[teamAbbr] || '#6B73FF' // Default purple
}

// Mobile detection utility
const isMobileDevice = () => {
  return window.innerWidth <= 767 // Using same breakpoint as PHONES media query
}

const GameCardComponent = ({ game }) => {
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)

  const gameStatus = getGameStatus(game)
  const gameTime = formatGameTime(game.startTime)
  const isUpcoming = gameStatus === 'INWIN'
  const isFinal = gameStatus === 'Final'

  // Handle scores for resolved games
  const teamAScore =
    game.resolved && game.teamAScore !== undefined ? game.teamAScore : null
  const teamBScore =
    game.resolved && game.teamBScore !== undefined ? game.teamBScore : null

  const displayTeamAScore = getDisplayScore(teamAScore)
  const displayTeamBScore = getDisplayScore(teamBScore)

  const teamAAbbr = getTeamAbbreviation(game.teamA, game.teamOne)
  const teamBAbbr = getTeamAbbreviation(game.teamB, game.teamTwo)

  const handleInWinClick = () => {
    if (isMobileDevice()) {
      setIsMobileModalOpen(true)
    } else {
      // For desktop, you could implement the existing modal here
      // or show a message that this feature is mobile-only
    }
  }

  const handleMobileModalClose = () => {
    setIsMobileModalOpen(false)
  }

  return (
    <ClassNames>
      {({ css, cx }) => (
        <>
          <div className={css(styles.cardContainer)}>
            <div className={css(styles.timeContainer)}>
              <Text size={13} className={css(styles.timeText)}>
                {gameTime}
              </Text>
              <Text size={10} className={css(styles.timeSubtext)}>
                PM
              </Text>
            </div>

            <div className={css(styles.teamsContainer)}>
              {/* Team A */}
              <div className={css(styles.teamSection)}>
                <div className={css(styles.teamWrapper)}>
                  <div className={css(styles.teamInfo)}>
                    <div className={css(styles.teamLogo)}>
                      {getTeamLogo(game.teamOne) ? (
                        <InWinImage
                          source={getTeamLogo(game.teamOne)}
                          alt={game.teamA}
                          className={css(styles.logoImage)}
                        />
                      ) : (
                        <div
                          className={cx(
                            css(styles.logoPlaceholder),
                            css({ backgroundColor: getTeamColor(teamAAbbr) })
                          )}
                        >
                          <Text size={9} bold className={css(styles.logoText)}>
                            {teamAAbbr}
                          </Text>
                        </div>
                      )}
                    </div>
                    <Text size={13} className={css(styles.teamText)}>
                      {teamAAbbr}
                    </Text>
                  </div>
                </div>
                {isFinal && displayTeamAScore !== null && (
                  <div className={css(styles.scoreContainer)}>
                    <Text size={14} bold className={css(styles.scoreText)}>
                      {displayTeamAScore}
                    </Text>
                  </div>
                )}
              </div>

              {/* Team B */}
              <div className={css(styles.teamSection)}>
                <div className={css(styles.teamWrapper)}>
                  <div className={css(styles.teamInfo)}>
                    <div className={css(styles.teamLogo)}>
                      {getTeamLogo(game.teamTwo) ? (
                        <InWinImage
                          source={getTeamLogo(game.teamTwo)}
                          alt={game.teamB}
                          className={css(styles.logoImage)}
                        />
                      ) : (
                        <div
                          className={cx(
                            css(styles.logoPlaceholder),
                            css({ backgroundColor: getTeamColor(teamBAbbr) })
                          )}
                        >
                          <Text size={9} bold className={css(styles.logoText)}>
                            {teamBAbbr}
                          </Text>
                        </div>
                      )}
                    </div>
                    <Text size={13} className={css(styles.teamText)}>
                      {teamBAbbr}
                    </Text>
                  </div>
                </div>
                {isFinal && displayTeamBScore !== null && (
                  <div className={css(styles.scoreContainer)}>
                    <Text size={14} bold className={css(styles.scoreText)}>
                      {displayTeamBScore}
                    </Text>
                  </div>
                )}
              </div>
            </div>

            {/* Vertical Divider */}
            <div className={css(styles.verticalDivider)}></div>

            <div className={css(styles.statusContainer)}>
              {isUpcoming ? (
                <button
                  className={css(styles.inwinButton)}
                  onClick={handleInWinClick}
                >
                  <Text size={13} bold className={css(styles.inwinText)}>
                    INWIN
                  </Text>
                </button>
              ) : (
                <Text size={11} className={css(styles.statusText)}>
                  {gameStatus}
                </Text>
              )}
            </div>
          </div>

          {/* Mobile InWin Modal */}
          <MobileInWinModal
            game={game}
            isOpen={isMobileModalOpen}
            onClose={handleMobileModalClose}
          />
        </>
      )}
    </ClassNames>
  )
}

GameCardComponent.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startTime: PropTypes.string,
    teamA: PropTypes.string,
    teamB: PropTypes.string,
    teamOne: PropTypes.shape({
      logo: PropTypes.string,
      teamName: PropTypes.string,
    }),
    teamTwo: PropTypes.shape({
      logo: PropTypes.string,
      teamName: PropTypes.string,
    }),
    resolved: PropTypes.bool,
    winner: PropTypes.string,
    teamAScore: PropTypes.number,
    teamBScore: PropTypes.number,
  }).isRequired,
}

export default GameCardComponent
